import React, { useEffect, useRef, useState } from 'react'
import styled, { injectGlobal } from 'styled-components'
import { Navigate, useLocation } from 'react-router-dom'
import { ReactComponent as LayersLogo } from '../assets/img/layers-logo-mono.svg'
import azureLogo from '../assets/img/logo-azure-blue.svg'
import { UIStyles } from '../theme'
import { LargeButton } from './UIComponents/buttons'
import { pillLooks } from './UIComponents/StyledButtons'
import userStoredData from '../userStoredData'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import { FALLBACK_LANGUAGE, TRANSLATED_LINKS } from '../constants'
import Routing from '../Routing'
import LanguageButtons from '../../src/components/UserComponents/LanguageButtons'
import InputLabel from '@mui/material/InputLabel'
import { TextField } from '@mui/material'
import capitalize from 'lodash/capitalize'
import { identifyUser } from './analytics/analytics'
import { trackForgotPasswordClicked } from './loginTracking'
import { buildIdentifyProps } from './loginIdentify'
import { createAzureLoginFlight } from './azureLogin'
import useAzureLogin from './useAzureLogin'
import useSessionAuth from '../useSessionAuth'
import { useDispatch, useSelector } from 'react-redux'
import { localeSet } from '../features/locale/localeSlice'
import { setMessagesFromLocale } from '@layers-frontend/commons/store/actions/messages'
import { getPrivacyAndCookiesUrlByLocale } from '@layers-frontend/commons/helpers/getPrivacyAndCookiesUrlByLocale'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import { LOGIN_CROP_MASTER_CONFIG } from '../loginCropMasterConfig'

const mdQueries = style => `@media (max-width: 1023px) { ${style} }`

const PageWrapper = styled.div`
  display: flex;
  position: relative;
  z-index: 5;
  height: 100vh;
  background: radial-gradient(119.22% 119.22% at 4.02% 8.7%, #4d2359 0%, #1c2541 36.46%, #1a3844 69.41%, #688245 100%);

  ${mdQueries(`
    overflow-y: auto;
    height: 100svh;
  `)}
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 32px;
  padding: 32px;
  max-width: 100%;
  width: 100%;
  height: 100%;
`

const LoginImageWrapper = styled.div`
  width: auto;
  height: 100%;
  display: flex;
  flex: 1 1 0;
  position: relative;

  ${mdQueries(`
    display: none;
  `)}
`

const LoginImageLink = styled.a`
  cursor: pointer;
  display: block;
  width: 100%;
  height: 100%;
`

const LoginImage = styled.img`
  border-radius: 30px;
  object-fit: cover;
  width: 100%;
  height: 100%;
`

const LoginImageTextOverlay = styled.div`
  position: absolute;
  bottom: 150px;
  left: 100px;
  display: flex;
  flex-direction: column;
  z-index: 10;

  @media (max-height: 700px) and (min-width: 1024px) {
    align-items: start;
    white-space: nowrap;
    left: 50%;
    top: 50%;
    bottom: auto;
    transform: translate(-50%, -50%);
  }

  ${mdQueries(`
    display: none;
  `)}
`

const LoginImageTextXl = styled.span`
  font-family: 'Raleway', sans-serif;
  font-size: 21pt;
  font-weight: 500;
  font-style: italic;
  color: white;
  line-height: 42px;
`

const LoginImageTextSm = styled.span`
  font-family: 'Raleway', sans-serif;
  font-size: 10pt;
  font-weight: 300;
  font-style: italic;
  color: white;
  line-height: 20px;
`

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  align-items: center;
  padding: 0 24px;

  ${mdQueries(`
    padding: 0;
  `)}
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  margin: 0;
  width: 100%;
  gap: 12px;
`

const LoginContentWrapper = styled.div`
  width: 435px;
  height: 100%;
  gap: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  position: relative;
  margin: auto;
  z-index: 5;
  max-height: 100vh;
  overflow-y: auto;

  ${mdQueries(`
    width: 100%;
    max-width: 435px;
    max-height: none;
    overflow: visible;
  `)}
`

const LanguageButtonsWrapper = styled.div`
  z-index: 6;
  width: 100%;
  display: flex;
  justify-content: end;
`

const HemavBrandingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: auto;
  & > img {
    margin-bottom: 0;
  }
`

const SeparatorWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 24px;
  cursor: default;
  padding: 0 45px;
`

const SeparatorLabel = styled.span`
  text-align: center;
  font-size: 16px;
  color: ${UIStyles.whiteColor};
`

const SeparatorLine = styled.div`
  display: flex;
  flex: 1;
  background-color: ${UIStyles.whiteTransparent015};
  height: 1px;
`

const AzureImg = styled.img`
  width: 20px;
  height: 20px;
`

// The mint confirm pill comes from the pill prop; this only sizes it for the form.
const StyledLargeButton = styled(LargeButton)`
  width: 100%;
  margin: 0;
  padding: 0;
  font-weight: 500;
  font-size: 16px;
  font-style: normal;
  height: 40px;
  border: 0;

  &:focus-visible {
    box-shadow: 0 0 0 3px ${UIStyles.mintColorTransparent};
  }
`

const StyledIcon = styled.i`
  padding-right: 4px;
  color: ${UIStyles.mintColor};
`

const LayersImg = styled(LayersLogo)`
  height: 144px;
  width: auto;
  color: ${UIStyles.whiteColor};
`

const LinkWrapper = styled.a`
  margin-bottom: 0;
  cursor: pointer;
  font-size: 16px;
  color: ${UIStyles.lightGrayColor};
  transition: color 0.15s;

  :focus {
    color: ${UIStyles.lightGrayColor};
    outline: none;
  }

  /* After :focus, so a link still focused from its click turns mint on hover too. */
  &:hover {
    color: ${UIStyles.mintColor};
    outline: none;
    text-decoration: underline;
  }

  &:focus-visible {
    color: ${UIStyles.mintColor};
  }
`

const CookiesAndPrivacyLinkWrapper = styled(LinkWrapper)`
  font-size: 16px;
  padding: 8px;
  text-align: center;
`

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Label = styled.span`
  font-size: 16px;
  color: ${UIStyles.lightGrayColor};
  margin-bottom: 0;
`

const PasswordLabelWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  /* Rendered after the input in the DOM so Tab jumps straight from username to
     password (the forgot-password link would otherwise sit between them);
     order pulls the label row visually back above the field. */
  order: -1;
`

const PasswordIcon = styled.i`
  color: ${UIStyles.whiteColor};
  font-size: 16px;
`

// The secondary actions (Azure, request a demo) as the navy pill.
const BaseLinkButton = styled.button`
  cursor: pointer;
  margin: 0;
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 12px;
  align-items: center;
  width: 100%;
  padding: 0;
  height: 40px;
  font-size: 16px;
  border: 0;
  ${pillLooks.neutral};

  /* Bootstrap underlines and recolours links on hover and focus; the link version stays a pill. */
  &:hover,
  &:focus {
    text-decoration: none;
  }

  &:focus {
    color: ${UIStyles.whiteTransparent08};
  }

  &:focus-visible {
    color: ${UIStyles.whiteColor};
    box-shadow: inset 0 0 0 1px ${UIStyles.mintBorder}, inset 0 0 0 999px ${UIStyles.whiteTransparent01};
  }
`

// Deep navy fields with the mint contour; mint on hover, and a mint ring while typing.
const StyledTextField = styled(TextField)`
  & .MuiOutlinedInput-root {
    height: 40px;
    color: ${UIStyles.whiteColor};
    border-radius: 12px;
    background-color: ${UIStyles.deepblueColor3};
    transition: box-shadow 0.15s;

    & fieldset {
      border-width: 1px;
      border-radius: 12px;
      border-color: ${UIStyles.mintBorder};
      transition: border-color 0.15s;
    }

    &:hover fieldset,
    &.Mui-focused fieldset {
      border-width: 1px;
      border-color: ${UIStyles.mintColor};
    }

    &.Mui-focused {
      box-shadow: 0 0 0 3px ${UIStyles.mintColorTransparent};
    }

    & .MuiOutlinedInput-input {
      color: ${UIStyles.whiteColor};
      font-size: 18px;
    }

    & .MuiIconButton-root {
      color: ${UIStyles.whiteColor};
    }

    & .MuiIconButton-root:hover {
      background-color: ${UIStyles.transparent};
    }

    & .MuiIconButton-root:hover ${PasswordIcon} {
      color: ${UIStyles.mintColor};
    }
  }
`

const StyledInputLabel = styled(InputLabel)`
  &.MuiInputLabel-root {
    margin-bottom: 0;
  }
`

const NewLoginLink = BaseLinkButton.withComponent('a')

const AzureButton = ({ t, flight, fetchApiKeyWithOauthToken, fetchProfileAndRedirect, errorNotification, azureOnly = false }) => {
  const azureLoginHandler = useAzureLogin({ flight, fetchApiKeyWithOauthToken, fetchProfileAndRedirect, errorNotification, t, azureOnly })

  return (
    <BaseLinkButton onClick={azureLoginHandler}>
      <AzureImg src={azureLogo} />
      <span>{t('Quick start with Azure')}</span>
    </BaseLinkButton>
  )
}

const Login = ({
  fetchProfile,
  t,
  isLoginLoading,
  isInvalidProfile,
  userName,
  user,
  isProfileLoading,
  fetchLogin,
  errorNotification,
  fetchApiKeyWithOauthToken,
  fetchSamplePrefix,
  locale,
  homeRoute
}) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const availableLocales = useSelector(state => state.config.i18n.availableLocales)

  const searchParams = new URLSearchParams(location.search)
  // to-do - add redirect from new layers to ?azureOnly=true
  const azureOnly = searchParams.get('azureOnly') === 'true' || false

  const [login, setLogin] = useState('')
  const [password, setPassword] = useState('')
  // How the platform session signed in, from the session cookie: survives reloads and new tabs.
  const auth = useSessionAuth()
  // One Azure token-to-session attempt at a time for the whole page (azureLogin.js). Owned here, not
  // by AzureButton: this component renders nothing while a login or the profile loads, which unmounts
  // the button — a flight of its own would be lost with it.
  const azureFlightRef = useRef(null)
  if (azureFlightRef.current === null) azureFlightRef.current = createAzureLoginFlight()

  const [showPassword, setShowPassword] = useState(false)

  const handleClickShowPassword = () => setShowPassword(show => !show)

  const handleMouseDownPassword = event => {
    event.preventDefault()
  }

  const handleMouseUpPassword = event => {
    event.preventDefault()
  }

  useEffect(() => {
    const storedLocale = userStoredData.locale
    const isStoredLocaleAvailable = availableLocales.some(available => storedLocale === available.locale)
    const _locale = isStoredLocaleAvailable ? storedLocale : FALLBACK_LANGUAGE

    dispatch(localeSet(_locale))
    dispatch(setMessagesFromLocale(_locale))
  }, [availableLocales, dispatch])

  useEffect(() => {
    injectGlobal`
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    input[type=number] {
      -moz-appearance: textfield;
    }
    input:-webkit-autofill,
    input:-webkit-autofill:focus {
      transition: background-color 600000s 0s, color 600000s 0s;
    }
    .loginInput input:-webkit-autofill,
    .loginInput input:-webkit-autofill:hover, 
    .loginInput input:-webkit-autofill:focus,
    .loginInput textarea:-webkit-autofill,
    .loginInput textarea:-webkit-autofill:hover,
    .loginInput textarea:-webkit-autofill:focus,
    .loginInput select:-webkit-autofill,
    .loginInput select:-webkit-autofill:hover,
    .loginInput select:-webkit-autofill:focus {
      border: 0;
      -webkit-text-fill-color: white;
      color: white;
      -webkit-box-shadow: unset;
      box-shadow: unset;
      background-color: transparent;
      transition: background-color 5000s ease-in-out 0s;
    }
  @media (max-width: 768px) {
    .loginInput{
    font-size: 26px !important;
    width: 456px !important;
    height: 82px !important;
    }
  }
  `
  }, [])

  useEffect(() => {
    if (userStoredData.API_KEY) {
      fetchProfileAndRedirect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (user && user.id) {
      identifyUser(`${user.id}`, buildIdentifyProps({ user, auth }))
    }
  }, [user, auth])

  const fetchProfileAndRedirect = token => {
    if (!userStoredData.API_KEY) {
      userStoredData.setToken(token)
    }

    fetchProfile()
      .then(() => {
        // The sample prefix (get_unique_prefix) is common to every page: requested here, once the profile
        // is in (it is kept by user id). Not awaited: the landing page does not wait for it, and a refusal
        // (a user disabled in mom) shows its own notification.
        fetchSamplePrefix().catch(() => {})
      })
      .catch(() => {
        userStoredData.removeToken()
      })
  }

  const loginHandler = () => {
    fetchLogin({ login, password })
      .then(fetchProfileAndRedirect)
      .catch(({ message }) => {
        const unauthorizedStatus = 401
        const isUnauthorized = message.includes(unauthorizedStatus)
        errorNotification(isUnauthorized ? t('Invalid credentials') : t('An error occurred from the server. Please try again'))
        setPassword('')
      })
  }

  const hasFilledUserAndPassword = () => {
    return !isEmpty(login) && !isEmpty(password)
  }

  const onKeyPressHandler = ({ key }) => {
    if (key === 'Enter' && hasFilledUserAndPassword) {
      loginHandler()
    }
  }

  const onLoginChange = login => {
    console.log(login)
    setLogin(login)
  }
  const onPasswordChange = password => setPassword(password)

  const hasLoggedIn = !!userName
  const isCheckingCredentials = isLoginLoading || userStoredData.API_KEY || isProfileLoading

  const contactHandler = () => {
    window.open(TRANSLATED_LINKS[userStoredData.locale], '_self')
  }

  if (hasLoggedIn) {
    // A deep link wins; otherwise the landing page is the user's home (by role, viewer by default).
    const previousePage = get(location, 'state.redirectedFrom')
    const redirectRoute = previousePage || `/${homeRoute}`

    return <Navigate to={redirectRoute} replace />
  }

  if (isCheckingCredentials) {
    return null
  }

  if (isInvalidProfile) {
    userStoredData.removeToken()
  }

  return (
    <PageWrapper>
      <ContentWrapper>
        <LoginImageWrapper>
          <LoginImageLink href={LOGIN_CROP_MASTER_CONFIG.getHref(locale)} target="_blank" rel="noopener noreferrer">
            <LoginImage src={LOGIN_CROP_MASTER_CONFIG.imageSrc} />
          </LoginImageLink>
          <LoginImageTextOverlay>
            <LoginImageTextXl>{t(LOGIN_CROP_MASTER_CONFIG.text1Key)}</LoginImageTextXl>
            <LoginImageTextXl>{t(LOGIN_CROP_MASTER_CONFIG.text2Key)}</LoginImageTextXl>
            <LoginImageTextSm>{t(LOGIN_CROP_MASTER_CONFIG.text3Key)}</LoginImageTextSm>
          </LoginImageTextOverlay>
        </LoginImageWrapper>

        <LoginContentWrapper>
          <LanguageButtonsWrapper>
            <LanguageButtons showGlobeIcon inputStyle={{ marginLeft: '10px' }} iconColor={UIStyles.whiteColor} iconMargin="8px" iconPadding="0" />
          </LanguageButtonsWrapper>

          <FormWrapper>
            <HemavBrandingWrapper>
              <LayersImg />
            </HemavBrandingWrapper>

            {!azureOnly ? (
              <Form onKeyDown={onKeyPressHandler}>
                <FieldWrapper>
                  <StyledInputLabel>
                    <StyledIcon className="fas fa-user" />
                    <Label>{t('Username')}</Label>
                  </StyledInputLabel>

                  <StyledTextField
                    variant="outlined"
                    onChange={e => onLoginChange(e.target.value)}
                    value={login}
                    id="input-user"
                    inputProps={{ 'data-testid': 'user-input' }}
                  />
                </FieldWrapper>

                <FieldWrapper>
                  <StyledTextField
                    variant="outlined"
                    onChange={e => onPasswordChange(e.target.value)}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    id="input-password"
                    inputProps={{ 'data-testid': 'password-input' }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={showPassword ? 'hide the password' : 'display the password'}
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            onMouseUp={handleMouseUpPassword}
                            sx={{ color: 'white' }}
                          >
                            {showPassword ? <PasswordIcon className="fa-regular fa-eye-slash" /> : <PasswordIcon className="fa-regular fa-eye" />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />

                  <PasswordLabelWrapper>
                    <StyledInputLabel>
                      <StyledIcon className="fas fa-lock" />
                      <Label>{t('Password')}</Label>
                    </StyledInputLabel>

                    <LinkWrapper
                      className="forget_password"
                      href={'//' + Routing.locationHostName + '/resetting/request'}
                      data-testid="forgot-password-link"
                      onClick={trackForgotPasswordClicked}
                    >
                      {t('Forgot password?')}
                    </LinkWrapper>
                  </PasswordLabelWrapper>
                </FieldWrapper>

                <StyledLargeButton label={t('Log in')} onClick={loginHandler} id="button-login" testId="login-button" pill="confirm" />
              </Form>
            ) : (
              /*  to-do - add redirect to new layers login */
              <NewLoginLink href={''}>{t('Login')}</NewLoginLink>
            )}

            <SeparatorWrapper>
              <SeparatorLine />
              <SeparatorLabel>{t('or')}</SeparatorLabel>
              <SeparatorLine />
            </SeparatorWrapper>

            <AzureButton
              t={t}
              flight={azureFlightRef.current}
              fetchApiKeyWithOauthToken={fetchApiKeyWithOauthToken}
              fetchProfileAndRedirect={fetchProfileAndRedirect}
              errorNotification={errorNotification}
              azureOnly={azureOnly}
            />

            <SeparatorWrapper>
              <SeparatorLine />
              <SeparatorLabel>{t('Not a user yet ?')}</SeparatorLabel>
              <SeparatorLine />
            </SeparatorWrapper>

            <BaseLinkButton onClick={contactHandler}>{capitalize(t('Request a demo'))}</BaseLinkButton>
          </FormWrapper>

          <CookiesAndPrivacyLinkWrapper href={getPrivacyAndCookiesUrlByLocale(locale)} target="_blank">
            {t('Cookies and Privacy')}
          </CookiesAndPrivacyLinkWrapper>
        </LoginContentWrapper>
      </ContentWrapper>
    </PageWrapper>
  )
}

export default Login
